import { API_BASE_URL } from '../config';
import { withFirebaseAuthHeaders } from '../authHeaders';

// externalId is GONE from this body.
//
// It used to carry getNextOrderId's browser-minted counter, and the gateway wrote
// it straight onto the order. The gateway no longer writes it at all: the field is
// reserved for the Stripe PaymentIntent id, which the webhook sets server-side.
// See src/utils/order.helper.ts, which has documented that reservation since the
// confirmation-number work.
type cutVideoType = {
  id: string;
  startTime: string;
  duration: number;
  customerId: string;
  orderId: string;
}

// HOW A CUT REQUEST CAME BACK. The request itself is unchanged; only what the
// caller is told about the answer is.
//
//   ok       a 2xx. The clip exists, or existed already.
//   refused  the gateway's 409: the order is not COMPLETED yet because the
//            webhook has not landed. Expected, and asked again later.
//   failed   anything else that is not a 2xx, or no response at all. The cut
//            itself did not happen. The gateway answers 500 with ffmpeg's own
//            message when the cut dies; the message is not read here.
//
// The orders page needs the distinction: it used to be told only that a
// request went out, and kept promising a download after three real failures.
//
// 'in-progress' is the gateway's 202: a cut for this order is already running
// on another instance (since 2026-10 a cut can take minutes, and each retry
// used to start another one). It is a request that was answered, and it is NOT
// a cut this request started, so the orders page does not count it against its
// three tries. See requestClipCutOnce.
export type CutVideoOutcome =
  | { outcome: 'ok'; result: unknown }
  | { outcome: 'in-progress' }
  | { outcome: 'refused' }
  | { outcome: 'failed'; status: number | null };

export const cutVideo = async (bodyRequest: cutVideoType): Promise<CutVideoOutcome> => {
  try{
    const url = `${API_BASE_URL}/video`;
    const headers = await withFirebaseAuthHeaders({
      'Content-Type': 'application/json',
    });
    const response = await fetch(
      url,
      {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(bodyRequest),
      }
    )

    if (response.ok) {
      const result = await response.json();
      return response.status === 202 || result?.inProgress === true
        ? { outcome: 'in-progress' }
        : { outcome: 'ok', result };
    }

    // TODO Error
    console.log('Error cutting the video', response)
    return response.status === 409
      ? { outcome: 'refused' }
      : { outcome: 'failed', status: response.status };
  } catch (e: any) {
    // TODO Error
    console.log('Error ', e.message)
    return { outcome: 'failed', status: null };
  }
};

/**
 * Ask the server to cut the clip for an order, at most once per browser session.
 *
 * TWO PAGES CALL THIS AND THAT IS DELIBERATE. The thank-you page fires on arrival
 * back from Stripe, which is when most buyers are present. The orders page fires
 * for any completed order that still has no clip, which is the backstop for the
 * buyer who closed the tab. Neither alone is enough: the first loses the buyer who
 * leaves, the second means nobody sees a clip until they navigate to Profile.
 *
 * THE GUARD IS WHY THIS WRAPPER EXISTS. The thank-you page's own "GO TO DOWNLOAD"
 * button navigates to Profile, so the normal flow is thank-you fires, the buyer
 * clicks through within a second or two, and the orders page fires again for the
 * same order while the first cut is still running. That is not a rare race, it is
 * the common path. sessionStorage makes the second call a no-op.
 *
 * IT IS A MITIGATION, NOT A LOCK, AND IT IS NARROWER THAN IT LOOKS.
 * sessionStorage is scoped to ONE top-level browsing context, so a second tab
 * opened to the same origin starts empty and is not guarded at all. Neither is a
 * second device. An earlier version of this comment claimed two tabs share it;
 * they do not. What it reliably covers is the common path, which is one tab going
 * thank-you page then GO TO DOWNLOAD then orders page.
 *
 * The server-side race it narrows is recorded as a finding, not fixed here,
 * because fixing it properly needs a claim field on the order and that is outside
 * this change.
 *
 * IT IS ALSO BEST EFFORT. A refusal is normal: the webhook is asynchronous, so an
 * order can still be DRAFT when the thank-you page loads, and the server answers
 * 409. Nothing is surfaced to the buyer, because the orders page will ask again
 * and it polls until a clip appears.
 *
 * IT RESOLVES 'skipped' WHEN NOTHING WENT OUT, and that return value is
 * load-bearing rather than incidental. The orders page caps how many times it will
 * ask, and a cap that counts CALLS instead of REQUESTS is spent by the no-ops this
 * function is built to produce: three calls at a 2.5 second poll all land inside
 * the two-minute TTL below, so all three can return here without a fetch and the
 * gate then closes permanently on a buyer who has already paid.
 *
 * Any other value means a request went out, and says how it came back: 'ok',
 * 'refused' (the gateway's 409, the webhook has not landed) or 'failed' (the cut
 * did not happen). A refusal still counts as a request. The cap exists to bound
 * real failures, and a 409 is the server answering. The orders page reads the
 * distinction so that it can stop promising a download once the budget is spent
 * on failures; nothing about WHEN a request is sent depends on it.
 */
export const CUT_REQUESTED_PREFIX = 'clipCutRequested:';

/**
 * How long a recorded request stays authoritative.
 *
 * WITHOUT THIS THE GUARD CAN STRAND A BUYER PERMANENTLY. The key is written
 * before the request goes out and cleared only after it comes back refused. If the
 * document is replaced in between, by a reload or a navigation, the in-flight
 * fetch dies with it, the clearing code never runs, and the key survives for the
 * life of the tab. Every later trigger then no-ops, including the orders page's
 * 2.5 second poll, and the buyer never gets a clip in that tab.
 *
 * Two minutes is comfortably longer than a cut takes and short enough that a
 * stranded key heals on its own.
 */
const CUT_REQUEST_TTL_MS = 2 * 60 * 1000;

/**
 * TWO ORDER SHAPES ARE LIVE AT ONCE AND THEY DISAGREE ABOUT WHAT `id` MEANS.
 * This is the trap that OrderDetailView.tsx documents at length, and getting it
 * wrong here is silent: the request simply goes out naming the wrong video, or
 * never goes out at all.
 *
 *   redux copy, arriving from checkout:  id = VIDEO id,  internalId = ORDER id
 *   API row, arriving from the orders list: id = ORDER id, productId = VIDEO id,
 *                                           and there is no internalId
 *
 * So the presence of internalId is what says which shape this is, and it is the
 * only reliable discriminator. When it is absent and productId is also absent we
 * have no video id at all, and guessing would request a cut against an order id.
 * Return instead.
 */
const resolveOrderAndVideoIds = (order: {
  id: string;
  internalId?: string;
  productId?: string;
}): { orderId?: string; videoId?: string } => {
  const orderId = order.internalId ?? order.id;
  const videoId = order.productId ?? (order.internalId ? order.id : undefined);

  return { orderId, videoId };
};

export type ClipCutOutcome = 'skipped' | CutVideoOutcome['outcome'];

export const requestClipCutOnce = async (order: {
  id: string;
  internalId?: string;
  productId?: string;
  startingTime: string;
  purchasedVideoDuration: number;
  videoUrl?: string;
}, customerId: string): Promise<ClipCutOutcome> => {
  const { orderId, videoId } = resolveOrderAndVideoIds(order);

  if (!orderId || !videoId || order.videoUrl) {
    return 'skipped';
  }

  const key = `${CUT_REQUESTED_PREFIX}${orderId}`;

  try {
    // A TIMESTAMP, not a flag. See CUT_REQUEST_TTL_MS: a bare flag written before
    // the request and cleared after it is stranded forever by a reload in between.
    const recordedAt = Number(sessionStorage.getItem(key));

    if (recordedAt && Date.now() - recordedAt < CUT_REQUEST_TTL_MS) {
      // NOTHING WENT OUT. The caller must not count this against its cap.
      return 'skipped';
    }

    sessionStorage.setItem(key, String(Date.now()));
  } catch (e) {
    // Private mode, or storage disabled. Fall through and ask anyway: a duplicate
    // request is a wasted cut, a skipped request is a buyer with no clip.
  }

  const cut = await cutVideo({
    id: videoId,
    startTime: order.startingTime,
    duration: order.purchasedVideoDuration,
    customerId,
    orderId,
  });

  // THE GUARD ONLY STICKS ON SUCCESS, and that is what makes the two triggers
  // cover each other rather than cancel each other.
  //
  // A refusal is expected: the webhook is asynchronous, so the thank-you page can
  // arrive while the order is still DRAFT and the server answers 409. If the guard
  // stuck on that, the orders page would no-op afterwards and the buyer would
  // never get a clip at all. Clearing it lets the next trigger, and the orders
  // page's own poll, ask again once the webhook has landed. A failure clears it
  // for the same reason: the next real ask is the retry.
  //
  // 'in-progress' keeps the guard too. A cut IS running, on another instance, so
  // asking again every 2.5 seconds would only make the server look the order up
  // each time. The guard's two-minute life is the retry: if that cut dies, the
  // next ask after it takes the order over.
  if (cut.outcome !== 'ok' && cut.outcome !== 'in-progress') {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      // Same as above. Nothing to do.
    }
  }

  // ANYTHING BUT 'skipped' MEANS "A REQUEST WENT OUT". The value says how it came
  // back; the thank-you page ignores that, the orders page reads it. See the note
  // on the return value at the top.
  return cut.outcome;
};

export const getVideo = async (videoId: string) => {
  try{  
    const url = `${API_BASE_URL}/video/${videoId}`;
    const headers = await withFirebaseAuthHeaders({
      'Content-Type': 'application/json',
    });
    const response = await fetch(
      url, 
      {
        method: 'GET',
        headers,
        credentials: 'include'
      }
    )

    if (response.ok) {
      const result = await response.json();
      return result;
    } else {
      // TODO Error
      console.log('Error getting the video', response)
    }
  } catch (e: any) {
    // TODO Error
    console.log('Error ', e.message)
  }
}
